import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
    Box, Flex, Heading, Button, VStack, FormControl, FormLabel, Input,
    Select as NativeSelect, Grid, Icon, HStack, Text, IconButton, Tag, TagLabel,
    TagCloseButton, Wrap, useToast, Spinner
} from '@chakra-ui/react';
import { Select as ChakraReactSelect, CreatableSelect } from 'chakra-react-select';
import { FaPaperclip, FaTimes, FaArrowLeft } from 'react-icons/fa';
import { getTicketMasterData, createTicket, createTicketCategory } from '../services/api';
import { WikiEditor, docHasContent } from '../components/WikiEditor';

const TicketCreatePage = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCreatingCategory, setIsCreatingCategory] = useState(false);
    
    const [masterData, setMasterData] = useState({ categories: [], users: [] });
    const [formData, setFormData] = useState({
        subject: '', description: '', priority: 'baixa',
        categories: [], assignees: [], dueDate: '',
    });
    const [files, setFiles] = useState([]);
    const [linkedItems, setLinkedItems] = useState([]);
    const [currentItemId, setCurrentItemId] = useState('');

    useEffect(() => {
        const fetchMasterData = async () => {
            setLoading(true);
            try {
                const response = await getTicketMasterData();
                setMasterData(response.data);
            } catch (error) {
                toast({ title: "Erro ao carregar dados do formulário.", status: 'error', isClosable: true });
            } finally {
                setLoading(false);
            }
        };
        fetchMasterData();
    }, [toast]);

    const categoryOptions = masterData.categories.map(cat => ({ value: cat.id, label: cat.name }));
    const userOptions = masterData.users.map(user => ({ value: user.id, label: user.name }));

    const handleCreateCategory = async (inputValue) => {
        const nome = (inputValue || '').trim();
        if (!nome) return;
        setIsCreatingCategory(true);
        try {
            const { data } = await createTicketCategory(nome);
            const novaOpcao = { value: data.id, label: data.name };
            setMasterData(prev => (
                prev.categories.some(c => c.id === data.id)
                    ? prev
                    : { ...prev, categories: [...prev.categories, { id: data.id, name: data.name }] }
            ));
            setFormData(prev => (
                prev.categories.some(c => c.value === data.id)
                    ? prev
                    : { ...prev, categories: [...prev.categories, novaOpcao] }
            ));
        } catch (error) {
            toast({ title: "Erro ao criar categoria.", status: 'error', isClosable: true });
        } finally {
            setIsCreatingCategory(false);
        }
    };

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    const handleFileChange = (e) => { if(e.target.files) { setFiles(prev => [...prev, ...Array.from(e.target.files)]); } };
    const removeFile = (fileName) => setFiles(prev => prev.filter(file => file.name !== fileName));
    const addLinkedItem = () => { if (currentItemId && !linkedItems.includes(currentItemId)) { setLinkedItems(prev => [...prev, currentItemId]); setCurrentItemId(''); } };
    const removeLinkedItem = (idToRemove) => setLinkedItems(prev => prev.filter(id => id !== idToRemove));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        const submissionData = new FormData();
        submissionData.append('subject', formData.subject);
        submissionData.append('description', formData.description);
        submissionData.append('priority', formData.priority);
        if(formData.dueDate) submissionData.append('dueDate', formData.dueDate);
        
        formData.categories.forEach(c => submissionData.append('categoryIds[]', c.value));
        formData.assignees.forEach(a => submissionData.append('assigneeIds[]', a.value));
        linkedItems.forEach(item => submissionData.append('linkedItems[]', item));
        files.forEach(file => submissionData.append('files[]', file));

        try {
            await createTicket(submissionData);
            toast({ title: "Ticket criado com sucesso!", status: 'success', isClosable: true });
            navigate('/tickets');
        } catch (error) {
            toast({
                title: "Erro ao criar ticket.",
                description: error.response?.data?.message || "Verifique os campos e tente novamente.",
                status: 'error', isClosable: true,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return <Flex justify="center" align="center" h="100vh"><Spinner size="xl" /></Flex>;
    }

    return (
        <Box p={{base: 4, md: 8}} bg="gray.50" minH="100vh">
           <Box maxW="1000px" mx="auto">
                <Flex mb={4}>
                    <Button as={RouterLink} to="/tickets" size="sm" leftIcon={<FaArrowLeft />}>Voltar para a Lista</Button>
                </Flex>
                <Box bg="white" p={8} borderRadius="md" shadow="sm">
                    <form onSubmit={handleSubmit}>
                        <VStack spacing={6} align="stretch">
                            <Heading as="h1" size="lg">Criar Novo Ticket</Heading>
                            <FormControl isRequired>
                                <FormLabel htmlFor="subject">Assunto</FormLabel>
                                <Input id="subject" name="subject" value={formData.subject} onChange={handleChange} />
                            </FormControl>
                            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={6}>
                                <FormControl isRequired>
                                    <FormLabel htmlFor="priority">Prioridade</FormLabel>
                                    <NativeSelect id="priority" name="priority" value={formData.priority} onChange={handleChange}>
                                        <option value="baixa">Baixa</option>
                                        <option value="media">Média</option>
                                        <option value="alta">Alta</option>
                                        <option value="urgente">Urgente</option>
                                    </NativeSelect>
                                </FormControl>
                                <FormControl>
                                    <FormLabel htmlFor="dueDate">Prazo (Opcional)</FormLabel>
                                    <Input id="dueDate" name="dueDate" type="date" value={formData.dueDate} onChange={handleChange}/>
                                </FormControl>
                            </Grid>
                            <FormControl isRequired>
                                <FormLabel htmlFor="categories">Categorias</FormLabel>
                                <CreatableSelect
                                    isMulti
                                    id="categories"
                                    name="categories"
                                    options={categoryOptions}
                                    placeholder="Selecione ou crie uma categoria..."
                                    value={formData.categories}
                                    onChange={(opts) => setFormData(prev => ({ ...prev, categories: opts || [] }))}
                                    onCreateOption={handleCreateCategory}
                                    isLoading={isCreatingCategory}
                                    isDisabled={isCreatingCategory}
                                    formatCreateLabel={(inputValue) => `Criar nova categoria "${inputValue}"`}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel htmlFor="description">Descrição</FormLabel>
                                <WikiEditor
                                    value={formData.description}
                                    onChange={({ json }) => setFormData(prev => ({ ...prev, description: docHasContent(json) ? JSON.stringify(json) : '' }))}
                                    placeholder="Descreva o ticket... Digite '/' para blocos, cole Markdown ou imagens."
                                    minHeight="260px"
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel htmlFor="assignees">Atribuir para</FormLabel>
                                <ChakraReactSelect isMulti id="assignees" name="assignees" options={userOptions} placeholder="Selecione..." value={formData.assignees} onChange={(opts) => setFormData(prev => ({ ...prev, assignees: opts || [] }))} />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Itens Vinculados</FormLabel>
                                <VStack align="stretch" p={4} borderWidth={1} borderRadius="md">
                                    <Wrap>{linkedItems.map(item => (<Tag size="lg" key={item} borderRadius="full" variant="solid" colorScheme="gray"><TagLabel>{item}</TagLabel><TagCloseButton onClick={() => removeLinkedItem(item)}/></Tag>))}</Wrap>
                                    <HStack><Input placeholder="Digite o ID" value={currentItemId} onChange={(e) => setCurrentItemId(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addLinkedItem(); } }}/><Button onClick={addLinkedItem} colorScheme="blue">Adicionar</Button></HStack>
                                </VStack>
                            </FormControl>
                            <FormControl>
                                <FormLabel>Anexos</FormLabel>
                                <Box p={4} borderWidth={1} borderRadius="md" borderStyle="dashed">
                                    <VStack spacing={3}>
                                        {files.map((file, index) => (
                                            <HStack key={index} w="full" justify="space-between">
                                                <Icon as={FaPaperclip} color="gray.500"/>
                                                <Text fontSize="sm" isTruncated>{file.name}</Text>
                                                <Text fontSize="xs" color="gray.500">{Math.round(file.size / 1024)} KB</Text>
                                                <IconButton size="xs" icon={<FaTimes />} aria-label="Remover arquivo" onClick={() => removeFile(file.name)}/>
                                            </HStack>
                                        ))}
                                        <Button as="label" htmlFor="file-upload" cursor="pointer" w="full" variant="outline" colorScheme="gray">Adicionar Arquivos</Button>
                                        <Input id="file-upload" type="file" multiple onChange={handleFileChange} display="none"/>
                                    </VStack>
                                </Box>
                            </FormControl>
                            <Flex justify="flex-end" mt={4}>
                                <Button variant="ghost" mr={3} onClick={() => navigate('/tickets')} isDisabled={isSubmitting}>Cancelar</Button>
                                <Button type="submit" colorScheme="blue" isLoading={isSubmitting} loadingText="Criando...">Criar Ticket</Button>
                            </Flex>
                        </VStack>
                    </form>
                </Box>
           </Box>
        </Box>
    );
};

export default TicketCreatePage;